import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import ReportIcon from '@mui/icons-material/esm/Description';
import GenerateIcon from '@mui/icons-material/esm/PlayArrow';
import ScheduleIcon from '@mui/icons-material/esm/Schedule';
import RefreshIcon from '@mui/icons-material/esm/Refresh';
import DeleteIcon from '@mui/icons-material/esm/Delete';
import EditIcon from '@mui/icons-material/esm/Edit';

import { PageHeader } from '../../components/layout';
import { DataTable, Column, Modal } from '../../components/common';
import reportsService, {
  ReportMetadata,
  ScheduledReport,
  GenerateReportRequest,
} from '../../services/api/reportsService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function ReportsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [reports, setReports] = useState<ReportMetadata[]>([]);
  const [scheduledReports, setScheduledReports] = useState<ScheduledReport[]>([]);
  const [selectedReport, setSelectedReport] = useState<ReportMetadata | null>(null);
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Generation parameters
  const [reportFormat, setReportFormat] = useState<'PDF' | 'EXCEL' | 'CSV'>('PDF');
  const [reportParameters, setReportParameters] = useState<Record<string, any>>({});

  const categories = ['ALL', 'FINANCIAL', 'OPERATIONS', 'COMPLIANCE', 'CUSTOM'];

  const fetchReports = async () => {
    try {
      setLoading(true);
      setError(null);

      const category = tabValue === 0 ? undefined : categories[tabValue];
      const [reportsData, scheduledData] = await Promise.all([
        reportsService.getAvailableReports(category),
        reportsService.getScheduledReports(),
      ]);

      setReports(reportsData);
      setScheduledReports(scheduledData);
    } catch (err: any) {
      console.error('Failed to fetch reports:', err);
      setError(err.response?.data?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [tabValue]);

  const handleGenerateReport = async () => {
    if (!selectedReport) return;

    try {
      setGenerating(true);

      const request: GenerateReportRequest = {
        reportId: selectedReport.id,
        parameters: reportParameters,
        format: reportFormat,
      };

      const blob = await reportsService.generateReport(request);

      // Download file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedReport.name.replace(/\s+/g, '-')}.${reportFormat.toLowerCase()}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setGenerateModalOpen(false);
      setSelectedReport(null);
      setReportParameters({});
    } catch (err: any) {
      console.error('Failed to generate report:', err);
      setError(err.response?.data?.message || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const handleToggleScheduledReport = async (id: string, enabled: boolean) => {
    try {
      await reportsService.toggleScheduledReport(id, enabled);
      await fetchReports();
    } catch (err: any) {
      console.error('Failed to toggle scheduled report:', err);
      setError('Failed to update scheduled report');
    }
  };

  const handleDeleteScheduledReport = async (id: string) => {
    try {
      await reportsService.deleteScheduledReport(id);
      await fetchReports();
    } catch (err: any) {
      console.error('Failed to delete scheduled report:', err);
      setError('Failed to delete scheduled report');
    }
  };

  // Scheduled reports table columns
  const scheduledColumns: Column<ScheduledReport>[] = [
    {
      id: 'reportName',
      label: 'Report Name',
      minWidth: 200,
    },
    {
      id: 'frequency',
      label: 'Frequency',
      minWidth: 100,
      format: (value: string) => (
        <Chip label={value} size="small" color="primary" variant="outlined" />
      ),
    },
    {
      id: 'nextRun',
      label: 'Next Run',
      minWidth: 150,
      format: (value: string) => new Date(value).toLocaleDateString(),
    },
    {
      id: 'recipients',
      label: 'Recipients',
      minWidth: 200,
      format: (value: string[]) => value.join(', '),
    },
    {
      id: 'enabled',
      label: 'Enabled',
      minWidth: 100,
      format: (value: boolean, row: ScheduledReport) => (
        <Switch
          checked={value}
          onChange={(e) => handleToggleScheduledReport(row.id, e.target.checked)}
          size="small"
        />
      ),
    },
    {
      id: 'id',
      label: 'Actions',
      minWidth: 120,
      format: (_value: string, row: ScheduledReport) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => console.log('Edit:', row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteScheduledReport(row.id)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="Reports & Analytics"
        breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Reports' }]}
        actions={
          <Tooltip title="Refresh">
            <IconButton onClick={fetchReports}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        }
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Category Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(_e, newValue) => setTabValue(newValue)}>
          {categories.map((category) => (
            <Tab key={category} label={category.charAt(0) + category.slice(1).toLowerCase()} />
          ))}
          <Tab label="Scheduled" />
        </Tabs>
      </Box>

      {/* Report Categories */}
      {categories.map((category, index) => (
        <TabPanel key={category} value={tabValue} index={index}>
          <Grid container spacing={3}>
            {reports
              .filter((r) => index === 0 || r.category === category)
              .map((report) => (
                <Grid item xs={12} sm={6} md={4} key={report.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        boxShadow: 4,
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <ReportIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" fontWeight="bold" gutterBottom>
                            {report.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {report.description}
                          </Typography>
                          {report.lastRun && (
                            <Typography variant="caption" color="text.secondary">
                              Last run: {new Date(report.lastRun).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<GenerateIcon />}
                          fullWidth
                          onClick={() => {
                            setSelectedReport(report);
                            setReportParameters(
                              report.parameters.reduce((acc, param) => {
                                acc[param.name] = param.defaultValue || '';
                                return acc;
                              }, {} as Record<string, any>)
                            );
                            setGenerateModalOpen(true);
                          }}
                        >
                          Generate
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<ScheduleIcon />}
                          onClick={() => {
                            setSelectedReport(report);
                            setScheduleModalOpen(true);
                          }}
                        >
                          Schedule
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>

          {reports.filter((r) => index === 0 || r.category === category).length === 0 && (
            <Alert severity="info">No reports available in this category.</Alert>
          )}
        </TabPanel>
      ))}

      {/* Scheduled Reports Tab */}
      <TabPanel value={tabValue} index={categories.length}>
        {scheduledReports.length === 0 ? (
          <Alert severity="info">No scheduled reports. Click "Schedule" on any report to create one.</Alert>
        ) : (
          <DataTable
            columns={scheduledColumns}
            data={scheduledReports}
            pagination
            searchable
            searchPlaceholder="Search scheduled reports..."
          />
        )}
      </TabPanel>

      {/* Generate Report Modal */}
      <Modal
        open={generateModalOpen}
        onClose={() => {
          setGenerateModalOpen(false);
          setSelectedReport(null);
          setReportParameters({});
        }}
        title={`Generate: ${selectedReport?.name}`}
        maxWidth="sm"
        actions={
          <>
            <Button
              onClick={() => {
                setGenerateModalOpen(false);
                setSelectedReport(null);
                setReportParameters({});
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleGenerateReport}
              disabled={generating}
              startIcon={generating ? <CircularProgress size={20} /> : <GenerateIcon />}
            >
              {generating ? 'Generating...' : 'Generate & Download'}
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Report Parameters */}
          {selectedReport?.parameters.map((param) => (
            <TextField
              key={param.name}
              label={param.label}
              type={param.type === 'DATE' ? 'date' : param.type === 'DATE_RANGE' ? 'date' : 'text'}
              value={reportParameters[param.name] || ''}
              onChange={(e) =>
                setReportParameters({ ...reportParameters, [param.name]: e.target.value })
              }
              required={param.required}
              InputLabelProps={
                param.type === 'DATE' || param.type === 'DATE_RANGE' ? { shrink: true } : undefined
              }
              fullWidth
              size="small"
            />
          ))}

          {/* Format Selection */}
          <FormControl fullWidth size="small">
            <InputLabel>Format</InputLabel>
            <Select
              value={reportFormat}
              label="Format"
              onChange={(e) => setReportFormat(e.target.value as any)}
            >
              <MenuItem value="PDF">PDF</MenuItem>
              <MenuItem value="EXCEL">Excel</MenuItem>
              <MenuItem value="CSV">CSV</MenuItem>
            </Select>
          </FormControl>

          {/* Include Charts Option (for PDF/Excel) */}
          {(reportFormat === 'PDF' || reportFormat === 'EXCEL') && (
            <FormControlLabel
              control={<Switch defaultChecked size="small" />}
              label="Include Charts"
            />
          )}
        </Box>
      </Modal>

      {/* Schedule Report Modal */}
      <Modal
        open={scheduleModalOpen}
        onClose={() => {
          setScheduleModalOpen(false);
          setSelectedReport(null);
        }}
        title={`Schedule: ${selectedReport?.name}`}
        maxWidth="sm"
        actions={
          <>
            <Button
              onClick={() => {
                setScheduleModalOpen(false);
                setSelectedReport(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={() => console.log('Create schedule')}>
              Create Schedule
            </Button>
          </>
        }
      >
        <Alert severity="info" sx={{ mb: 2 }}>
          Schedule configuration will be implemented here.
        </Alert>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Frequency</InputLabel>
            <Select defaultValue="WEEKLY" label="Frequency">
              <MenuItem value="DAILY">Daily</MenuItem>
              <MenuItem value="WEEKLY">Weekly</MenuItem>
              <MenuItem value="MONTHLY">Monthly</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Recipients (comma-separated emails)"
            placeholder="user1@example.com, user2@example.com"
            fullWidth
            size="small"
          />
        </Box>
      </Modal>
    </Box>
  );
}
